import * as React from "react";
import Table from "@amzn/awsui-components-react/polaris/table";
import Header from "@amzn/awsui-components-react/polaris/header";
import Button from "@amzn/awsui-components-react/polaris/button";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import Box from "@amzn/awsui-components-react/polaris/box";
import Modal from "@amzn/awsui-components-react/polaris/modal";
import FormField from "@amzn/awsui-components-react/polaris/form-field";
import Input from "@amzn/awsui-components-react/polaris/input";
import Toggle from "@amzn/awsui-components-react/polaris/toggle";
import Flashbar from "@amzn/awsui-components-react/polaris/flashbar";
import Alert from "@amzn/awsui-components-react/polaris/alert";
import StatusIndicator from "@amzn/awsui-components-react/polaris/status-indicator";
import ExpandableSection from "@amzn/awsui-components-react/polaris/expandable-section";
import ColumnLayout from "@amzn/awsui-components-react/polaris/column-layout";
import Textarea from "@amzn/awsui-components-react/polaris/textarea";
import Spinner from "@amzn/awsui-components-react/polaris/spinner";
import Link from "@amzn/awsui-components-react/polaris/link";
import { API } from "aws-amplify";
import { getAuthUsername } from "./identity";
import { fetchAllConfigs } from "./cpcp-config-api";
import { SelectOption, matchesSelection } from "./feature-type-filter";
import FeatureTypeFilters from "./FeatureTypeFilters";
import { recipeViewerUrl } from "./RecipeViewer";

interface ConfigItem {
  featureName: string;
  customerTypeName: string;
  minimumCustomerTypeCount?: number;
  maxCreationCount?: number;
  enabled?: boolean;
  retrievable?: boolean;
  recipeKey?: string;
  expiryDays?: number;
  stepWaitSeconds?: number;
  description?: string;
  ownerEmail?: string;
}

type SortKey = keyof ConfigItem;

interface State {
  configs: ConfigItem[];
  loading: boolean;
  error: string | null;
  editOpen: boolean;
  editItem: ConfigItem | null;
  filterFeature: SelectOption | null;
  filterType: SelectOption | null;
  saving: boolean;
  saveError: string | null;
  sortBy: SortKey;
  sortAsc: boolean;
}

class ConfigManager extends React.Component<any, State> {
  constructor(props: any) {
    super(props);
    this.state = {
      configs: [],
      loading: true,
      error: null,
      editOpen: false,
      editItem: null,
      filterFeature: null,
      filterType: null,
      saving: false,
      saveError: null,
      sortBy: 'featureName',
      sortAsc: true,
    };
  }

  componentDidMount() {
    this.loadConfigs();
  }

  loadConfigs = async () => {
    this.setState({ loading: true, error: null });
    try {
      const configs = await fetchAllConfigs<ConfigItem>();
      this.setState({ configs, loading: false });
    } catch (e) {
      this.setState({ error: e instanceof Error ? e.message : "Failed to load configs", loading: false });
    }
  };

  handleEdit = (item: ConfigItem) => {
    this.setState({ editOpen: true, editItem: { ...item }, saveError: null });
  };

  handleSave = async () => {
    const { editItem } = this.state;
    if (!editItem) return;
    if (editItem.ownerEmail && !/^[a-zA-Z0-9._-]+@amazon\.com$/.test(editItem.ownerEmail)) {
      this.setState({ saveError: "Owner Team Email must be a valid @amazon.com email" });
      return;
    }
    if (editItem.minimumCustomerTypeCount !== undefined && editItem.minimumCustomerTypeCount > 200) {
      this.setState({ saveError: "Min Count cannot exceed 200" });
      return;
    }
    if (editItem.minimumCustomerTypeCount !== undefined && editItem.minimumCustomerTypeCount < 0) {
      this.setState({ saveError: "Min Count cannot be negative" });
      return;
    }
    if (editItem.maxCreationCount !== undefined && editItem.maxCreationCount > 10) {
      this.setState({ saveError: "Max Creation Count cannot exceed 10" });
      return;
    }
    if (editItem.maxCreationCount !== undefined && editItem.maxCreationCount < 0) {
      this.setState({ saveError: "Max Creation Count cannot be negative" });
      return;
    }
    this.setState({ saving: true, saveError: null });
    try {
      const updatedBy = await getAuthUsername();
      if (!updatedBy) {
        this.setState({ saveError: "Unable to determine your identity. Please refresh the page and try again.", saving: false });
        return;
      }
      await API.put("CpcpV2API",
        `/cpcp/v2/config/${editItem.featureName}/${editItem.customerTypeName}`,
        { body: { ...editItem, updatedBy } });
      this.setState({ editOpen: false, editItem: null });
      this.loadConfigs();
    } catch (e) {
      this.setState({ saveError: e instanceof Error ? e.message : "Failed to save" });
    } finally {
      this.setState({ saving: false });
    }
  };

  handleFieldChange = (field: string, value: any) => {
    this.setState(prev => ({
      editItem: prev.editItem ? { ...prev.editItem, [field]: value } : null,
    }));
  };

  render() {
    const { configs, loading, error, editOpen, editItem, filterFeature, filterType, saving, saveError, sortBy, sortAsc } = this.state;

    if (loading) return <Spinner size="large" />;

    const selection = { feature: filterFeature, type: filterType };
    const filtered = configs.filter(cfg => matchesSelection(cfg, selection)).sort((a, b) => {
      const av = a[sortBy];
      const bv = b[sortBy];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      let cmp: number;
      if (typeof av === 'boolean') cmp = Number(av) - Number(bv as boolean);
      else if (typeof av === 'number') cmp = av - (bv as number);
      else cmp = String(av).localeCompare(String(bv));
      return sortAsc ? cmp : -cmp;
    });

    return (
      <SpaceBetween size="l">
        <Header variant="h2">Config Manager</Header>
        <Alert type="info" header="Resource Limits">
          Min Count is capped at <b>100</b> and Max Create at <b>10</b> per customer type.
          Please configure these values responsibly — each cached customer consumes test environment
          resources and Step Function executions. Set Min Count to the smallest pool size your team actually needs.
        </Alert>
        {error && <Flashbar items={[{ type: "error", content: error, dismissible: true, onDismiss: () => this.setState({ error: null }) }]} />}

        <ExpandableSection headerText="Field Glossary">
          <ColumnLayout columns={2}>
            <Box><b>Feature Name</b> — Top-level grouping (e.g., Manual)</Box>
            <Box><b>Customer Type Name</b> — Customer variant (e.g., CustomerWithCheckout)</Box>
            <Box><b>Minimum Customer Type Count</b> — Target READY count to maintain</Box>
            <Box><b>Max Creation Count</b> — Max new customers per monitor cycle</Box>
            <Box><b>Enabled</b> — Auto-create customers for this type</Box>
            <Box><b>Retrievable</b> — Customers claimable via GetCustomer API</Box>
            <Box><b>Recipe Key</b> — S3 key of the recipe JSON</Box>
            <Box><b>Expiry Days</b> — Days until READY customers expire</Box>
            <Box><b>Step Wait Seconds</b> — Wait between recipe steps</Box>
            <Box><b>Description</b> — Summary of what this customer type includes</Box>
          </ColumnLayout>
        </ExpandableSection>

        <FeatureTypeFilters
          items={configs}
          selection={{ feature: filterFeature, type: filterType }}
          onChange={next => this.setState({ filterFeature: next.feature, filterType: next.type })}
          placeholder="All"
        />

        <Table
          header={
            <Header counter={`(${filtered.length}/${configs.length})`}
              actions={<Button iconName="refresh" onClick={this.loadConfigs} />}>
              Config Manager
            </Header>
          }
          sortingColumn={{ sortingField: sortBy }}
          sortingDescending={!sortAsc}
          onSortingChange={({ detail }) => {
            const field = detail.sortingColumn.sortingField as SortKey | undefined;
            if (!field) return;
            this.setState(prev => ({
              sortBy: field,
              sortAsc: prev.sortBy === field ? !prev.sortAsc : true,
            }));
          }}
          columnDefinitions={[
            { id: "actions", header: "Actions", cell: (item: ConfigItem) => (
              <Button iconName="edit" variant="icon" onClick={() => this.handleEdit(item)} />
            ), width: 70 },
            { id: "featureName", header: "Feature Name", cell: (item: ConfigItem) => item.featureName, sortingField: "featureName" },
            { id: "customerTypeName", header: "Customer Type Name", cell: (item: ConfigItem) => <b>{item.customerTypeName}</b>, sortingField: "customerTypeName" },
            { id: "minCount", header: "Min Count", cell: (item: ConfigItem) => item.minimumCustomerTypeCount, sortingField: "minimumCustomerTypeCount" },
            { id: "maxCreate", header: "Max Create", cell: (item: ConfigItem) => item.maxCreationCount, sortingField: "maxCreationCount" },
            { id: "enabled", header: "Enabled", cell: (item: ConfigItem) => <StatusIndicator type={item.enabled ? "success" : "stopped"}>{item.enabled ? "Yes" : "No"}</StatusIndicator>, sortingField: "enabled" },
            { id: "retrievable", header: "Retrievable", cell: (item: ConfigItem) => <StatusIndicator type={item.retrievable !== false ? "success" : "stopped"}>{item.retrievable !== false ? "Yes" : "No"}</StatusIndicator>, sortingField: "retrievable" },
            { id: "recipeKey", header: "Recipe Key", cell: (item: ConfigItem) => item.recipeKey ? (
              <Link href={recipeViewerUrl(item.recipeKey)}
                onFollow={e => {
                  e.preventDefault();
                  window.open(e.detail.href, "_blank", "noopener,width=1000,height=800");
                }}>
                {item.recipeKey}
              </Link>
            ) : "—", sortingField: "recipeKey" },
            { id: "expiryDays", header: "Expiry Days", cell: (item: ConfigItem) => item.expiryDays, sortingField: "expiryDays" },
            { id: "waitSeconds", header: "Wait (s)", cell: (item: ConfigItem) => item.stepWaitSeconds, sortingField: "stepWaitSeconds" },
            { id: "description", header: "Description", cell: (item: ConfigItem) => item.description || "—", minWidth: 300, sortingField: "description" },
            { id: "ownerEmail", header: "Owner Email", cell: (item: ConfigItem) => item.ownerEmail || "—", sortingField: "ownerEmail" },
          ]}
          items={filtered}
          empty={<Box textAlign="center" color="inherit">No configs found</Box>}
        />

        <Modal
          visible={editOpen}
          onDismiss={() => this.setState({ editOpen: false })}
          header={`Edit Config — ${editItem?.featureName}/${editItem?.customerTypeName}`}
          footer={
            <Box float="right">
              <SpaceBetween size="xs" direction="horizontal">
                <Button variant="link" onClick={() => this.setState({ editOpen: false })}>Cancel</Button>
                <Button variant="primary" onClick={this.handleSave} loading={saving}>Save</Button>
              </SpaceBetween>
            </Box>
          }
        >
          <SpaceBetween size="m">
            {saveError && <Flashbar items={[{ type: "error", content: saveError }]} />}
            <ColumnLayout columns={2}>
              <FormField label="Feature Name"><Input value={editItem?.featureName || ""} disabled /></FormField>
              <FormField label="Customer Type Name"><Input value={editItem?.customerTypeName || ""} disabled /></FormField>
              <FormField label="Min Count" constraintText="Range: 0–200"
                errorText={(editItem?.minimumCustomerTypeCount ?? 0) > 200 ? "Must be 200 or less" : (editItem?.minimumCustomerTypeCount ?? 0) < 0 ? "Cannot be negative" : undefined}>
                <Input type="number" value={String(editItem?.minimumCustomerTypeCount ?? "")} onChange={({ detail }) => { const parsed = parseInt(detail.value, 10); this.handleFieldChange("minimumCustomerTypeCount", detail.value === "" ? undefined : isNaN(parsed) ? undefined : parsed); }} />
              </FormField>
              <FormField label="Max Creation Count" constraintText="Range: 0–10"
                errorText={(editItem?.maxCreationCount ?? 0) > 10 ? "Must be 10 or less" : (editItem?.maxCreationCount ?? 0) < 0 ? "Cannot be negative" : undefined}>
                <Input type="number" value={String(editItem?.maxCreationCount ?? "")} onChange={({ detail }) => { const parsed = parseInt(detail.value, 10); this.handleFieldChange("maxCreationCount", detail.value === "" ? undefined : isNaN(parsed) ? undefined : parsed); }} />
              </FormField>
            </ColumnLayout>
            <SpaceBetween size="s" direction="horizontal">
              <Toggle checked={editItem?.enabled ?? false} onChange={({ detail }) => this.handleFieldChange("enabled", detail.checked)}>Enabled</Toggle>
              <Toggle checked={editItem?.retrievable ?? true} onChange={({ detail }) => this.handleFieldChange("retrievable", detail.checked)}>Retrievable</Toggle>
            </SpaceBetween>
            <FormField label="Recipe Key"><Input value={editItem?.recipeKey || ""} onChange={({ detail }) => this.handleFieldChange("recipeKey", detail.value)} /></FormField>
            <ColumnLayout columns={2}>
              <FormField label="Expiry Days"><Input type="number" value={String(editItem?.expiryDays ?? "")} onChange={({ detail }) => this.handleFieldChange("expiryDays", parseInt(detail.value) || 0)} /></FormField>
              <FormField label="Step Wait Seconds"><Input type="number" value={String(editItem?.stepWaitSeconds ?? "")} onChange={({ detail }) => this.handleFieldChange("stepWaitSeconds", parseInt(detail.value) || 0)} /></FormField>
            </ColumnLayout>
            <FormField label="Description"><Textarea value={editItem?.description || ""} onChange={({ detail }) => this.handleFieldChange("description", detail.value)} rows={3} /></FormField>
            <FormField label="Owner Team Email"
              errorText={editItem?.ownerEmail && !/^[a-zA-Z0-9._-]+@amazon\.com$/.test(editItem.ownerEmail) ? "Must be a valid @amazon.com email" : undefined}>
              <Input value={editItem?.ownerEmail || ""} onChange={({ detail }) => this.handleFieldChange("ownerEmail", detail.value)} placeholder="team-alias@amazon.com" />
            </FormField>
          </SpaceBetween>
        </Modal>

      </SpaceBetween>
    );
  }
}

export default ConfigManager;
